import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { confirmOrderDelivery, fetchOrderSuggestions, fetchOrders, uploadPaymentSlip } from '../../api/orders';
import { submitReview, updateReview } from '../../api/reviews';
import ClientReviewSummary from '../../components/reviews/ClientReviewSummary';
import ReviewModal from '../../components/reviews/ReviewModal';
import { getFriendlyError } from '../../utils/friendlyError';
import OrderDetailDrawer from '../../components/orders/OrderDetailDrawer';
import OrderStatusStepper from '../../components/orders/OrderStatusStepper';
import DashboardShell from '../../components/ui/DashboardShell';
import PaginatedFooter from '../../components/ui/PaginatedFooter';
import { DEFAULT_PAGE_SIZE } from '../../constants/pagination';
import ListSearchToolbar from '../../components/ui/ListSearchToolbar';
import { useListSearch } from '../../hooks/useListSearch';
import { useToast } from '../../context/ToastContext';
import ClientPaymentSlipUpload from '../../components/payments/ClientPaymentSlipUpload';
import { getPaymentPolicy, slipAllowsUpload } from '../../utils/paymentSlip';

const ClientOrdersPage = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [editReview, setEditReview] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const {
    input: searchInput,
    setInput: setSearchInput,
    appliedSearch,
    commitSearch,
    suggestions,
    suggestionsLoading,
  } = useListSearch({
    queryKeyPrefix: ['client-order-suggestions'],
    fetchSuggestions: (q) => fetchOrderSuggestions({ q }),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['orders', 'client', page, pageSize, appliedSearch],
    queryFn: () =>
      fetchOrders({
        page,
        limit: pageSize,
        search: appliedSearch || undefined,
      }),
  });

  const uploadMut = useMutation({
    mutationFn: ({ orderId, file, bankAccountId }) =>
      uploadPaymentSlip(orderId, file, { bankAccountId }),
    onSuccess: () => {
      showToast(t('toast.paymentSlipUploaded'), 'success');
      setPreview(null);
      setUploadingId(null);
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
    onError: (err) => showToast(err.response?.data?.message || t('errors.uploadFailed'), 'error'),
  });

  const orders = data?.orders || [];
  const pagination = data?.pagination;

  const canConfirmDelivery = (order) =>
    ['PACKING', 'SHIPPED', 'OUT_FOR_DELIVERY'].includes(order.status);

  const deliveryMut = useMutation({
    mutationFn: (orderId) => confirmOrderDelivery(orderId),
    onSuccess: () => {
      showToast(t('toast.orderDelivered'), 'success');
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
    onError: (err) => showToast(getFriendlyError(err, t('toast.confirmDeliveryFailed')), 'error'),
  });

  const reviewMut = useMutation({
    mutationFn: submitReview,
    onSuccess: () => {
      showToast(t('toast.reviewSubmitted'), 'success');
      setReviewTarget(null);
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['my-reviews'] });
    },
    onError: (err) => showToast(getFriendlyError(err, t('toast.errorGeneric')), 'error'),
  });

  const updateReviewMut = useMutation({
    mutationFn: ({ id, body }) => updateReview(id, body),
    onSuccess: () => {
      showToast(t('toast.reviewUpdated'), 'success');
      setEditReview(null);
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['my-reviews'] });
    },
    onError: (err) => showToast(getFriendlyError(err, t('toast.errorGeneric')), 'error'),
  });

  return (
    <DashboardShell title={t('orders.myOrders')} subtitle={t('orders.myOrdersSubtitle')}>
      <ListSearchToolbar
        searchInput={searchInput}
        onSearchInputChange={setSearchInput}
        onSearch={(v) => { commitSearch(v); setPage(1); }}
        suggestions={suggestions}
        suggestionsLoading={suggestionsLoading}
        searchPlaceholder={t('orders.client.searchPlaceholder')}
        showFiltersToggle={false}
      />

      {isLoading && <p className="text-zinc-600 dark:text-zinc-500">{t('common.loading')}</p>}

      <div className="space-y-4">
        {orders.map((order) => (
          <div key={order._id} className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="font-medium">{t('orders.drawer.orderNumber', { number: order._id.slice(-8).toUpperCase() })}</p>
                <p className="text-sm text-zinc-600 dark:text-zinc-500">
                  {new Date(order.createdAt).toLocaleString()} · {t('common.currency', { amount: order.totalAmount?.toLocaleString() })}
                </p>
              </div>
              <span className="rounded-full bg-zinc-100 dark:bg-zinc-800 px-3 py-1 text-xs">{t(`status.order.${order.status}`)}</span>
            </div>

            <div className="mt-3 flex gap-1 overflow-x-auto">
              {order.items?.map((item, i) => (
                <div key={i} className="h-12 w-12 shrink-0 rounded bg-zinc-100 dark:bg-zinc-800 text-center text-[10px] leading-[3rem] text-zinc-600 dark:text-zinc-500">
                  {item.productName?.slice(0, 2)}
                </div>
              ))}
            </div>

            <div className="mt-4">
              <OrderStatusStepper status={order.status} />
            </div>

            {order.status === 'DELIVERED' && !order.review && (
              <button
                type="button"
                onClick={() => setReviewTarget(order)}
                className="mt-3 rounded-lg border border-amber-700/50 px-3 py-1.5 text-xs text-amber-300"
              >
                {t('reviews.reviewOrder')}
              </button>
            )}
            {order.review && (
              <ClientReviewSummary
                review={order.review}
                onEdit={() =>
                  setEditReview({ ...order.review, salonName: order.salonName })
                }
              />
            )}

            <div className="mt-4 flex flex-wrap gap-2">
              {canConfirmDelivery(order) && (
                <button
                  type="button"
                  disabled={deliveryMut.isPending}
                  onClick={() => deliveryMut.mutate(order._id)}
                  className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-500 disabled:opacity-50"
                >
                  {t('client.appointments.receivedOrder')}
                </button>
              )}
              {slipAllowsUpload(getPaymentPolicy(order)) && !['CANCELLED', 'REJECTED'].includes(order.status) && (
                <div className="w-full rounded-lg border border-zinc-200 p-3 dark:border-zinc-800">
                  <ClientPaymentSlipUpload
                    bankAccounts={order.bankAccounts || []}
                    uploading={uploadingId === order._id && uploadMut.isPending}
                    onUpload={(file, bankAccountId) => {
                      setPreview(URL.createObjectURL(file));
                      setUploadingId(order._id);
                      uploadMut.mutate({ orderId: order._id, file, bankAccountId });
                    }}
                  />
                </div>
              )}
              <button
                type="button"
                onClick={() => setSelectedId(order._id)}
                className="rounded-lg bg-glow-600 px-3 py-1.5 text-xs text-white"
              >
                {t('client.appointments.trackChat')}
              </button>
            </div>

            {uploadingId === order._id && preview && (
              <p className="mt-2 text-xs text-emerald-400">{t('toast.uploadingSlip')}</p>
            )}
          </div>
        ))}

        {!isLoading && orders.length === 0 && (
          <p className="text-zinc-600 dark:text-zinc-500">{t('empty.noOrders')}</p>
        )}
      </div>

      <PaginatedFooter
        pagination={pagination}
        page={page}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
      />

      <OrderDetailDrawer
        orderId={selectedId}
        onClose={() => setSelectedId(null)}
        salonMode={false}
      />

      <ReviewModal
        open={!!reviewTarget}
        title={t('reviews.reviewOrder')}
        subtitle={reviewTarget ? t('reviews.orderAtSalon', { salon: reviewTarget.salonName || '' }) : ''}
        onClose={() => setReviewTarget(null)}
        saving={reviewMut.isPending}
        onSubmit={({ rating, comment }) =>
          reviewMut.mutate({
            type: 'PRODUCT',
            tenantId: reviewTarget.tenantId?._id || reviewTarget.tenantId,
            orderId: reviewTarget._id,
            rating,
            comment,
          })
        }
      />

      <ReviewModal
        open={!!editReview}
        title={t('reviews.editReview')}
        subtitle={editReview ? t('reviews.orderAtSalon', { salon: editReview.salonName || '' }) : ''}
        initialRating={editReview?.rating || 0}
        initialComment={editReview?.comment || ''}
        submitLabel={t('common.save')}
        onClose={() => setEditReview(null)}
        saving={updateReviewMut.isPending}
        onSubmit={({ rating, comment }) =>
          updateReviewMut.mutate({ id: editReview._id, body: { rating, comment } })
        }
      />
    </DashboardShell>
  );
};

export default ClientOrdersPage;
